import { useEffect, useState } from 'react';
import api from '../api';
import {
  ClipboardCheck, ExternalLink, RefreshCw, CheckCircle2, XCircle,
  Clock, FileText, Loader2, Search,
} from 'lucide-react';

/**
 * Admin tab: review uploaded LPN Clinical Affiliation Forms.
 * Verifying a form releases the student's LPN Certificate of Completion.
 */
const ClinicalFormsPanel = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [reviewing, setReviewing] = useState(null);
  const [decision, setDecision] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status_filter: filter };
      const r = await api.get('/admin/clinical-affiliation-uploads', { params });
      setItems(r.data?.items || []);
    } catch (_) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submitDecision = async (id, status) => {
    setBusyId(id);
    try {
      await api.post(`/admin/clinical-affiliation-uploads/${id}/verify`, {
        decision: status,
        note: status === 'rejected' ? (decision[id] || '').trim() : null,
      });
      await load();
      setReviewing(null);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not update upload.');
    } finally {
      setBusyId(null);
    }
  };

  const filtered = items.filter((it) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (it.user_full_name || '').toLowerCase().includes(s) ||
      (it.user_email || '').toLowerCase().includes(s) ||
      (it.id || '').toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid="clinical-forms-panel" style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, color: '#0b2545', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClipboardCheck size={22} /> LPN Clinical Affiliation Forms
          </h2>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.86rem' }}>
            Verify a student's signed Clinical Affiliation Form to release their LPN Certificate of Completion.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
            <input
              data-testid="clinical-forms-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, or ID"
              style={{ padding: '8px 10px 8px 30px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.85rem' }}
            />
          </div>
          {['all', 'pending', 'verified', 'rejected'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`clinical-forms-filter-${f}`}
              style={{
                padding: '7px 12px',
                fontSize: '0.78rem',
                borderRadius: 6,
                border: '1px solid ' + (filter === f ? '#0b2545' : '#cbd5e1'),
                background: filter === f ? '#0b2545' : 'white',
                color: filter === f ? 'white' : '#475569',
                fontWeight: 700,
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >
              {f}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="clinical-forms-refresh"
            style={{ padding: 8, borderRadius: 6, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}
            title="Refresh"
          >
            <RefreshCw size={14} color="#475569" />
          </button>
        </div>
      </div>

      <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
          <thead style={{ background: '#f1f5f9' }}>
            <tr>
              <th style={th}>Student</th>
              <th style={th}>Uploaded</th>
              <th style={th}>Status</th>
              <th style={th}>File</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                No uploaded forms yet.
              </td></tr>
            ) : filtered.map((it) => {
              const meta = STATUS_META[it.status] || STATUS_META.pending;
              const dlUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin/clinical-affiliation-uploads/${it.id}/file`;
              return (
                <tr key={it.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={td}>
                    <div style={{ fontWeight: 700, color: '#0b2545' }}>{it.user_full_name || '—'}</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{it.user_email}</div>
                  </td>
                  <td style={td}>
                    <div>{new Date(it.uploaded_at).toLocaleDateString()}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{new Date(it.uploaded_at).toLocaleTimeString()}</div>
                  </td>
                  <td style={td}>
                    <span style={{ ...statusPill, background: meta.bg, color: meta.fg }}>
                      {meta.icon} {meta.label}
                    </span>
                    {it.verified_by && (
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
                        by {it.verified_by}
                      </div>
                    )}
                  </td>
                  <td style={td}>
                    <a
                      href={dlUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`clinical-file-${it.id}`}
                      style={{ color: '#1e40af', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.82rem', textDecoration: 'none', fontWeight: 600 }}
                    >
                      <FileText size={12} /> View PDF <ExternalLink size={10} />
                    </a>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      {!it.size ? '—' : it.size < 1024 ? '<1 KB' : `${Math.round(it.size / 1024)} KB`}
                    </div>
                  </td>
                  <td style={td}>
                    {it.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => submitDecision(it.id, 'verified')}
                          data-testid={`verify-${it.id}`}
                          disabled={busyId === it.id}
                          style={smallBtn('#22c55e')}
                        >
                          <CheckCircle2 size={12} /> Verify
                        </button>
                        <button
                          onClick={() => setReviewing(reviewing === it.id ? null : it.id)}
                          data-testid={`reject-toggle-${it.id}`}
                          style={smallBtn('#ef4444')}
                        >
                          <XCircle size={12} /> Reject
                        </button>
                      </div>
                    )}
                    {it.status !== 'pending' && (
                      <button
                        onClick={() => submitDecision(it.id, 'pending')}
                        data-testid={`reopen-${it.id}`}
                        disabled={busyId === it.id}
                        style={{ ...smallBtn('#475569'), background: '#f1f5f9', color: '#475569' }}
                      >
                        Re-open
                      </button>
                    )}
                    {reviewing === it.id && (
                      <div style={{ marginTop: 8 }}>
                        <textarea
                          rows={2}
                          placeholder="Reason for rejection (sent in admin record)"
                          data-testid={`reject-note-${it.id}`}
                          value={decision[it.id] || ''}
                          onChange={(e) => setDecision({ ...decision, [it.id]: e.target.value })}
                          style={{ width: '100%', padding: 6, fontSize: '0.78rem', border: '1px solid #cbd5e1', borderRadius: 6 }}
                        />
                        <button
                          onClick={() => submitDecision(it.id, 'rejected')}
                          data-testid={`reject-confirm-${it.id}`}
                          disabled={busyId === it.id}
                          style={{ ...smallBtn('#ef4444'), marginTop: 6 }}
                        >
                          Confirm Rejection
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const STATUS_META = {
  pending:  { label: 'Pending', bg: '#dbeafe', fg: '#1e3a8a', icon: <Clock size={12} /> },
  verified: { label: 'Verified', bg: '#dcfce7', fg: '#065f46', icon: <CheckCircle2 size={12} /> },
  rejected: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b', icon: <XCircle size={12} /> },
};
const th = { padding: '12px 14px', textAlign: 'left', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '12px 14px', verticalAlign: 'top' };
const statusPill = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700 };
const smallBtn = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 });

export default ClinicalFormsPanel;
